import { isNotDefined } from '@sgme/fp';
import { SgwtAccountCenter, useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { type ReactNode, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import LinksDropDown from '@/App/Header/LinksDropDown.tsx';
import { NotificationZone } from '@/App/NotificationZone/index.tsx';
import UserPreferences from '@/App/UserPreferences/index.tsx';
import { VoiceTimestampingHeaderButton } from '@/App/VoiceTimestamping/VoiceTimestampingHeaderButton.tsx';
import { getConfig } from '@/config/config.ts';
import { saveThemeToLocalStorage, updateTheme } from '@/utils/theme.ts';

export const APP_HEADER_ID = 'header';

type Props = {
  onLanguageChanged: (language: string) => void;
};

export function Header({ onLanguageChanged }: Props): ReactNode {
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const onOpenCalendar = useCallback(
    () => sgwtWebAnalytics?.trackEvent('Header', 'Open cambist calendar'),
    [sgwtWebAnalytics],
  );

  const [accountCenterIsReady, setAccountCenterIdReady] = useState(false);

  return (
    <header id={APP_HEADER_ID} className="navbar bg-lvl1">
      <div className="navbar-title">
        <a className="navbar-title-link" href="/">
          <div className="navbar-logo">
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="SG Logo">
              <rect x="0" y="0" width="32" height="32" fill="white" />
              <rect x="1" y="1" width="30" height="15" fill="var(--bs-socgen)" />
              <rect x="1" y="16" width="30" height="15" fill="black" />
              <rect x="6" y="15" width="20" height="2" fill="white" />
            </svg>
          </div>

          <div className="navbar-title-divider" />

          <div className="navbar-service-name">Foreign Exchange</div>
        </a>
      </div>

      <div className="navbar-content">
        <ul className="navbar-navigation m-0 gap-3">
          <li className="navbar-item">
            <a
              href={getConfig().app.calendarUrl}
              className="navbar-link"
              target="_blank"
              rel="noreferrer"
              onClick={onOpenCalendar}
            >
              <FormattedMessage id="header.cambistCalendar" values={outIcon} />
            </a>
          </li>

          <li className="navbar-item">
            <LinksDropDown />
          </li>
        </ul>
      </div>

      <div className="navbar-toolbar">
        <VoiceTimestampingHeaderButton />
        <UserPreferences />
        <SgwtAccountCenter
          //debug
          availableLanguages={['fr', 'en']}
          authentication="sg-connect"
          mode="sg-markets"
          producerCode={import.meta.env.VITE_PRODUCER_CODE}
          themeSwitcher
          onThemeChanged={(e) => {
            const theme = e.themeChosen;
            saveThemeToLocalStorage(theme);
            updateTheme(theme);
          }}
          onWidgetReady={(_) => {
            if (accountCenterIsReady) {
              return;
            }
            setAccountCenterIdReady(true);
            window.SGWTWidgetConfiguration.bus.subscribe('global.language', (language: string | undefined) => {
              if (isNotDefined(language)) {
                return;
              }
              onLanguageChanged(language);
            });
          }}
          onLanguageChanged={onLanguageChanged}
        />
      </div>
      <NotificationZone />
    </header>
  );
}

const outIcon = { out: <i className="icon icon-sm ms-1">launch</i> };
